import React from 'react'
import { Link } from 'react-router-dom'
import { AttendeeStack } from '../AttendeeStack'
import { useAddToCalendar } from '../../hooks'

// Date, plus the time of day when the item actually has one. An item at exactly local
// midnight is treated as date-only: that's how a day-precision source arrives (a
// kayentaStories story parses "YYYY-MM-DD" to local midnight), and printing "00:00" on it
// would be noise. Anything else — a recruitment interview slot, say — is a real
// appointment, and its time is the most useful thing on the row.
const formatDate = (iso) => {
  const d = new Date(iso)
  const dayMonth = d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })
  if (d.getHours() === 0 && d.getMinutes() === 0) return dayMonth
  return `${dayMonth}, ${d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}`
}

// Compact row for an aggregated item from another app (see spa/src/hooks/useActivity.js) —
// reuses the same .ev-list-item/-main/-date/-title classes EventListItem uses, for a
// consistent look. The colored source marker is applied by the wrapping row in
// ActivityWidget.js, not here — this component only renders content.
//
// An item may opt into participation (see the provider contract in recruitment's
// helpers/calendarEvents.js), in which case it gets the same avatar-stack toggle and .ics
// offer a native event row has: a colleague can sign up for an interview here rather than
// having to open Rekrytering's Intervjuprocess page. Items that don't — a Kayenta Stories
// story — render exactly as before.
//
// Gate on `participation` being PRESENT, never on how many attendees it has: AttendeeStack
// deliberately draws a dashed "+" at zero, which is the affordance for an interview nobody has
// joined yet, and a stray "+" on a story row would just be a mystery button.
//
// The structure mirrors EventListItem's on purpose. The whole row used to be one <Link>; a
// <button> inside an <a> is invalid HTML and a nested-interactive a11y failure, so the link
// now covers only the text. With a single child this renders identically to before —
// .ev-list-item is already display:flex; justify-content:space-between.
export const ExternalEventItem = ({ item, onToggleParticipation }) => {
  const { offerAddToCalendar } = useAddToCalendar()

  // Signing up (not cancelling) offers the .ics, same as marking interest in an event.
  const handleToggle = async () => {
    const res = await onToggleParticipation(item)
    if (res && res.userIsAttending && item.ics) offerAddToCalendar(item.ics)
  }

  return (
    <div className="ev-list-item">
      <Link to={item.url} className="ev-list-item-main">
        <div className="ev-list-item-date">
          <span className="ev-list-item-datetime">{formatDate(item.start)}</span>
          <span className="ev-list-item-source"> · {item.sourceLabel}</span>
        </div>
        <div className="ev-list-item-title">{item.title}</div>
      </Link>

      {item.participation && onToggleParticipation &&
        <div className="ev-list-item-actions">
          <AttendeeStack
            users={item.participation.attendees || []}
            isParticipant={item.participation.userIsAttending}
            onToggle={handleToggle}
            joinTitle={item.participation.joinTitle}
            leaveTitle={item.participation.leaveTitle}
          />
        </div>
      }
    </div>
  )
}
